import { type TrackModel } from '@/shared/api'
import { ROUTES } from '@/shared/config'
import { showDeleteConfirmationModal } from '@/shared/lib/antd-utils'
import { navigateToFx } from '@/shared/lib/providers'
import { COMMON_COLUMN_WIDTH } from '@/shared/lib/styling'
import {
  CloseCircleTwoTone,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleTwoTone,
} from '@ant-design/icons'
import {
  Button,
  Flex,
  Space,
  type TableProps,
  Tooltip,
  Typography,
  theme,
} from 'antd'
import { useUnit } from 'effector-react'

import { TrackUploadStatus } from '../../lib'
import {
  $areTracksTableRowsSelected,
  selectedTracksDeleteRequested,
} from '../../model'
import { trackDeleted } from '../../model/track.model'

interface SortState {
  field: string | null
  order: 'asc' | 'desc' | null
}

interface useTracksTableColumnsProps {
  onEdit: (trackId: string) => void
  sort: SortState
}

export const useTracksTableColumns = ({
  onEdit: handleEdit,
  sort,
}: useTracksTableColumnsProps): TableProps<TrackModel>['columns'] => {
  const { token: antDesignThemeToken } = theme.useToken()

  const deleteTrack = useUnit(trackDeleted)
  const deleteSelectedTracks = useUnit(selectedTracksDeleteRequested)
  const areRowsSelected = useUnit($areTracksTableRowsSelected)

  const handleDelete = (id: string) => {
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteTrack({ id })
      },
    })
  }

  const handleDeleteSelected = () => {
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteSelectedTracks()
      },
      options: {
        title: 'Удаление',
        content: 'Вы уверены, что хотите удалить выбранные записи?',
        okText: 'Удалить',
      },
    })
  }

  const columns: TableProps<TrackModel>['columns'] = [
    {
      title: 'Наименование',
      width: '33%',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder:
        sort.field === 'name'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, track) => {
        if (track.uploadStatus === TrackUploadStatus.Uploading) {
          return (
            <Flex gap={16}>
              <Tooltip title="Файл не загружен">
                <ExclamationCircleTwoTone
                  twoToneColor={antDesignThemeToken.colorError}
                />
              </Tooltip>
              {track.name}
            </Flex>
          )
        } else if (track.uploadStatus === TrackUploadStatus.DeletionPending) {
          return (
            <Flex gap={16}>
              <Tooltip title="Помечен на удаление">
                <CloseCircleTwoTone
                  twoToneColor={antDesignThemeToken.colorWarning}
                />
              </Tooltip>
              {track.name}
            </Flex>
          )
        } else return track.name
      },
    },
    {
      title: 'Автор',
      width: '33%',
      dataIndex: 'author',
      key: 'author',
      sorter: true,
      sortOrder:
        sort.field === 'author'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, track) => {
        if (!track.authors) return ''
        const mainAuthors = track.authors.filter((a) => !a.isCoauthor)
        const coAuthors = track.authors.filter((a) => a.isCoauthor)
        const authors = [...mainAuthors, ...coAuthors]
        return (
          <>
            {authors.map((author, i) => {
              const isLast = i === authors.length - 1
              const route = ROUTES.AUTHORS.EDIT.replace(':id', author.id)

              return (
                <span key={author.id}>
                  <Typography.Link
                    onClick={() => void navigateToFx({ to: route })}
                  >
                    {author.nickname}
                  </Typography.Link>
                  {!isLast && ',\u00A0'}
                </span>
              )
            })}
          </>
        )
      },
    },
    {
      title: 'Альбом',
      width: '33%',
      dataIndex: 'album',
      key: 'album',
      sorter: true,
      sortOrder:
        sort.field === 'album'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, track) => {
        const album = track.album
        if (!album) return null
        const route = ROUTES.COLLECTIONS.EDIT_ALBUM.replace(
          ':id',
          album.albumId,
        )
        return (
          <span key={album.albumId}>
            <Typography.Link onClick={() => void navigateToFx({ to: route })}>
              {album.name}
            </Typography.Link>
          </span>
        )
      },
    },
    {
      title: (
        <>
          <Button
            disabled={!areRowsSelected}
            onClick={handleDeleteSelected}
            icon={
              <DeleteOutlined style={areRowsSelected ? { color: 'red' } : {}} />
            }
          />
        </>
      ),
      width: COMMON_COLUMN_WIDTH.ACTIONS,
      key: 'actions',
      align: 'center',
      render: (_, track) => (
        <Space size="middle">
          <Button
            onClick={() => {
              handleEdit(track.id)
            }}
            icon={<EditOutlined />}
          />
          <Button
            onClick={() => handleDelete(track.id)}
            icon={<DeleteOutlined style={{ color: 'red' }} />}
          />
        </Space>
      ),
    },
  ]

  return columns
}
